'use client';

import { createContact } from '@/server/mutations/app.mutation';
import { useTranslations } from 'next-intl';

import TextAreaField from '@/components/forms/fields/TextAreaField';
import TextField from '@/components/forms/fields/TextField';
import Form from '@/components/forms/Form';
import { Locale } from '@/i18n/config';
import { useServerAction } from '@/hooks/useServerAction';
import { ContactSchema } from '@/validations/app.validations';
import { cn } from '@/utils/app.utils';

import { IContactInput } from '@/types/app.type';
import Stack from '@/components/Stack';
import Button from '@/components/buttons/Button';
import CheckboxField from '@/components/forms/fields/CheckboxField';

export type ContactFormProps = {
  className?: string;
  locale?: Locale;
};
const ContactForm = ({
  className,
  locale,
}: ContactFormProps) => {
  const tContact = useTranslations('Contact');

  const {
    form,
    onSubmit,
    sentSuccessMessage,
    error,
    loading,
  } = useServerAction<IContactInput, typeof ContactSchema, typeof createContact, Locale[]>({
    schema: ContactSchema,
    action: createContact,
    bind: [locale!],
    sentMessage: tContact('messageSent'),
    translationKey: 'Contact',
    recaptchaActionName: 'contactForm',
    initialValue: { email: '', name: '', message: '', acceptNewsLetter: false },
  });

  return (
    <Form
      action={onSubmit}
      className={cn('flex flex-col', className)}
      error={error}
      form={form}
      loading={loading}
      successMessage={sentSuccessMessage}
      withButton={false}
    >
      <Stack className="self-stretch gap-6" direction="row" spacing={6}>
        <TextField
          required
          label={tContact('name')}
          name="name"
          placeholder={tContact('yourFullName')}
          rootClassName="flex-1"
        />
        <div className="flex-1 max-md:hidden" />
      </Stack>
      <Stack className="self-stretch gap-6" direction="row" spacing={6}>
        <TextField
          required
          label={tContact('email')}
          name="email"
          placeholder={tContact('email')}
          rootClassName="flex-1"
          type="email"
        />
        <TextField
          label={tContact('phoneNumber')}
          name="phone"
          placeholder={tContact('phoneNumberExample')}
          rootClassName="flex-1"
        />
      </Stack>
      <TextAreaField
        required
        label={tContact('message')}
        name="message"
        placeholder={tContact('writeSomething')}
        rows={5}
      />
      <div>
        <Stack className="lg:mt-16" direction="row" spacing={3}>
          <div className="flex-1">
            <CheckboxField
              label={tContact('acceptNewsLetter')}
              name="acceptNewsLetter"
            />
          </div>
          <div className="flex flex-col gap-4">
            <Button
              color="primary"
              disabled={loading}
              type="submit"
              variant="contained"
            >
              {tContact('send')}
            </Button>
          </div>
        </Stack>
      </div>
    </Form>
  );
};

export default ContactForm;
